import { theme } from 'antd';
import type { ThemeConfig } from 'antd';

export type ThemeMode = 'light' | 'dark';
export type ThemeSchemeKey =
  | 'ocean'
  | 'emerald'
  | 'violet'
  | 'sunset'
  | 'tealCare'
  | 'aquaHealth'
  | 'clinicalBlue'
  | 'mintLeaf'
  | 'navyCore'
  | 'silverSoft'
  | 'skyLight'
  | 'amberCare'
  | 'coralAlert'
  | 'auroraPulse'
  | 'royalSunrise'
  | 'forestBreeze'
  | 'twilightGlow'
  | 'coralReef'
  | 'goldenDawn'
  | 'mysticPurple'
  | 'rubyRed'
  | 'indigoDusk'
  | 'slateStorm'
  | 'copperEarth'
  | 'roseVivid'
  | 'heartbeatRed'
  | 'sageHerbal'
  | 'labPurple'
  | 'pharmaBlue'
  | 'platinumClinical';

type SchemePalette = {
  primary: string;
  secondary: string;
  warm: string;
  gradient?: string;
};

const BASE_FONT_FAMILY = `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'`;
const BASE_FONT_FAMILY_CODE = `'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace`;

export const THEME_SCHEME_KEYS: ThemeSchemeKey[] = [
  'ocean',
  'emerald',
  'violet',
  'sunset',
  'tealCare',
  'aquaHealth',
  'clinicalBlue',
  'mintLeaf',
  'navyCore',
  'silverSoft',
  'skyLight',
  'amberCare',
  'coralAlert',
  'auroraPulse',
  'royalSunrise',
  'forestBreeze',
  'twilightGlow',
  'coralReef',
  'goldenDawn',
  'mysticPurple',
  'rubyRed',
  'indigoDusk',
  'slateStorm',
  'copperEarth',
  'roseVivid',
  'heartbeatRed',
  'sageHerbal',
  'labPurple',
  'pharmaBlue',
  'platinumClinical',
];

export const THEME_COLOR_SCHEME_KEYS: ThemeSchemeKey[] = [
  'navyCore',
  'silverSoft',
  'skyLight',
  'amberCare',
  'coralAlert',
];

const NORMALIZED_SCHEME_MAP: Record<string, ThemeSchemeKey> = THEME_SCHEME_KEYS.reduce(
  (acc, key) => {
    acc[key.toLowerCase()] = key;
    return acc;
  },
  {} as Record<string, ThemeSchemeKey>
);

const SCHEME_PALETTES: Record<ThemeSchemeKey, SchemePalette> = {
  ocean: {
    primary: '#0F85AE',
    secondary: '#61AEB2',
    warm: '#F59E0B',
    gradient: 'linear-gradient(135deg, rgb(15, 133, 174) 0%, rgb(97, 174, 178) 50%, rgb(123, 225, 250) 100%)',
  },
  emerald: {
    primary: '#15803D',
    secondary: '#84CC16',
    warm: '#F97316',
  },
  violet: {
    primary: '#7C3AED',
    secondary: '#EC4899',
    warm: '#F59E0B',
  },
  sunset: {
    primary: '#EA580C',
    secondary: '#EF4444',
    warm: '#EAB308',
  },
  tealCare: {
    primary: '#0F766E',
    secondary: '#14B8A6',
    warm: '#FB7185',
  },
  aquaHealth: {
    primary: '#0E7490',
    secondary: '#38BDF8',
    warm: '#22C55E',
  },
  clinicalBlue: {
    primary: '#1E40AF',
    secondary: '#93C5FD',
    warm: '#06B6D4',
  },
  mintLeaf: {
    primary: '#059669',
    secondary: '#A3E635',
    warm: '#F97316',
  },
  navyCore: {
    primary: '#1b2250',
    secondary: '#1b2250',
    warm: '#a5c7fa',
    gradient: '#1b2250',
  },
  silverSoft: {
    primary: '#a3a3a3',
    secondary: '#a3a3a3',
    warm: '#e3a152',
    gradient: '#a3a3a3',
  },
  skyLight: {
    primary: '#047cfc',
    secondary: '#040c3c',
    warm: '#1b2250',
    gradient: 'linear-gradient(135deg, #047cfc 0%, #047cfc 30%, #040c3c 100%)',
  },
  amberCare: {
    primary: '#e3a152',
    secondary: '#e3a152',
    warm: '#1b2250',
    gradient: '#e3a152',
  },
  coralAlert: {
    primary: '#fe6047',
    secondary: '#fe6047',
    warm: '#1b2250',
    gradient: '#fe6047',
  },
  auroraPulse: {
    primary: '#6D28D9',
    secondary: '#06B6D4',
    warm: '#22C55E',
    gradient: 'linear-gradient(135deg, #6D28D9 0%, #06B6D4 45%, #22C55E 100%)',
  },
  royalSunrise: {
    primary: '#1D4ED8',
    secondary: '#F97316',
    warm: '#EC4899',
    gradient: 'linear-gradient(135deg, #1D4ED8 0%, #F97316 55%, #EC4899 100%)',
  },
  forestBreeze: {
    primary: '#228B22',
    secondary: '#32CD32',
    warm: '#ADFF2F',
    gradient: 'linear-gradient(135deg, #228B22 0%, #32CD32 50%, #ADFF2F 100%)',
  },
  twilightGlow: {
    primary: '#FF4500',
    secondary: '#FF6347',
    warm: '#FFD700',
    gradient: 'linear-gradient(135deg, #FF4500 0%, #FF6347 50%, #FFD700 100%)',
  },
  coralReef: {
    primary: '#FF7F50',
    secondary: '#FF4500',
    warm: '#FFDAB9',
    gradient: 'linear-gradient(135deg, #FF7F50 0%, #FF4500 50%, #FFDAB9 100%)',
  },
  goldenDawn: {
    primary: '#FFD700',
    secondary: '#FFA500',
    warm: '#FF8C00',
    gradient: 'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF8C00 100%)',
  },
  mysticPurple: {
    primary: '#6A0DAD',
    secondary: '#D8BFD8',
    warm: '#9370DB',
    gradient: 'linear-gradient(135deg, #6A0DAD 0%, #9370DB 50%, #D8BFD8 100%)',
  },
  rubyRed: {
    primary: '#BE123C',
    secondary: '#FB7185',
    warm: '#FCA5A5',
    gradient: 'linear-gradient(135deg, #BE123C 0%, #E11D48 50%, #FB7185 100%)',
  },
  indigoDusk: {
    primary: '#4338CA',
    secondary: '#818CF8',
    warm: '#A5B4FC',
    gradient: 'linear-gradient(135deg, #4338CA 0%, #6366F1 50%, #818CF8 100%)',
  },
  slateStorm: {
    primary: '#475569',
    secondary: '#94A3B8',
    warm: '#CBD5E1',
    gradient: 'linear-gradient(135deg, #334155 0%, #475569 45%, #64748B 100%)',
  },
  copperEarth: {
    primary: '#92400E',
    secondary: '#D97706',
    warm: '#FCD34D',
    gradient: 'linear-gradient(135deg, #78350F 0%, #92400E 40%, #B45309 100%)',
  },
  roseVivid: {
    primary: '#DB2777',
    secondary: '#F472B6',
    warm: '#FBCFE8',
    gradient: 'linear-gradient(135deg, #9D174D 0%, #DB2777 45%, #F472B6 100%)',
  },
  // ── Healthcare / Medical themed ─────────────────────────────────────
  heartbeatRed: {
    primary: '#DC2626',
    secondary: '#FCA5A5',
    warm: '#FEE2E2',
    gradient: 'linear-gradient(135deg, #991B1B 0%, #DC2626 45%, #F87171 100%)',
  },
  sageHerbal: {
    primary: '#4D7C0F',
    secondary: '#84CC16',
    warm: '#D9F99D',
    gradient: 'linear-gradient(135deg, #3F6212 0%, #4D7C0F 40%, #65A30D 100%)',
  },
  labPurple: {
    primary: '#9C27B0',
    secondary: '#CE93D8',
    warm: '#F3E5F5',
    gradient: 'linear-gradient(135deg, #6A1B9A 0%, #9C27B0 45%, #CE93D8 100%)',
  },
  pharmaBlue: {
    primary: '#0288D1',
    secondary: '#4FC3F7',
    warm: '#E1F5FE',
    gradient: 'linear-gradient(135deg, #01579B 0%, #0288D1 45%, #4FC3F7 100%)',
  },
  platinumClinical: {
    primary: '#546E7A',
    secondary: '#90A4AE',
    warm: '#CFD8DC',
    gradient: 'linear-gradient(135deg, #37474F 0%, #546E7A 45%, #78909C 100%)',
  },
};

const hexToRgb = (hex: string): [number, number, number] => {
  const normalized = hex.replace('#', '');
  const value = normalized.length === 3
    ? normalized.split('').map((part) => `${part}${part}`).join('')
    : normalized;
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return [r, g, b];
};

const withAlpha = (hex: string, alpha: number) => {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const normalizeThemeScheme = (value: unknown): ThemeSchemeKey => {
  if (typeof value !== 'string') return 'ocean';
  return NORMALIZED_SCHEME_MAP[value.toLowerCase()] ?? 'ocean';
};

export const getSchemePalette = (scheme: ThemeSchemeKey): SchemePalette => {
  return SCHEME_PALETTES[normalizeThemeScheme(scheme)];
};

const baseTokens = (mode: ThemeMode, scheme: ThemeSchemeKey): NonNullable<ThemeConfig['token']> => {
  const palette = getSchemePalette(scheme);
  const isDark = mode === 'dark';

  return {
    colorPrimary: palette.primary,
    colorInfo: palette.secondary,
    colorLink: palette.primary,
    colorLinkHover: palette.secondary,
    colorLinkActive: palette.primary,
    colorSuccess: '#52c41a',
    colorWarning: '#faad14',
    colorError: '#ff4d4f',
    colorTextBase: isDark ? '#F3F4F6' : '#0F172A',
    colorBgBase: isDark ? '#0A0A0A' : '#F8FAFC',
    borderRadius: 8,
    controlHeight: 40,
    controlHeightLG: 44,
    fontSize: 14,
    fontFamily: BASE_FONT_FAMILY,
    fontFamilyCode: BASE_FONT_FAMILY_CODE,
    sizeStep: 4,
    sizeUnit: 4,
  };
};

export const buildSchemeThemeConfig = (
  scheme: ThemeSchemeKey,
  mode: ThemeMode,
  overrides?: ThemeConfig
): ThemeConfig => {
  const palette = getSchemePalette(scheme);
  const isDark = mode === 'dark';
  const algorithm = isDark ? theme.darkAlgorithm : theme.defaultAlgorithm;

  const tokens = {
    ...baseTokens(mode, scheme),
    ...overrides?.token,
  };

  const baseComponents: NonNullable<ThemeConfig['components']> = {
    Layout: {
      headerBg: isDark ? '#111827' : '#FFFFFF',
      siderBg: isDark ? '#0F172A' : '#FFFFFF',
      bodyBg: isDark ? '#020617' : '#F8FAFC',
      footerBg: isDark ? '#020617' : '#F8FAFC',
    },
    Menu: {
      itemSelectedBg: withAlpha(palette.primary, isDark ? 0.26 : 0.12),
      itemHoverBg: withAlpha(palette.primary, isDark ? 0.16 : 0.06),
      itemSelectedColor: palette.primary,
      itemHoverColor: palette.primary,
      darkItemSelectedBg: withAlpha(palette.primary, 0.3),
      darkItemHoverBg: withAlpha(palette.primary, 0.16),
      darkItemSelectedColor: '#E2E8F0',
      darkItemHoverColor: '#E2E8F0',
      darkItemBg: 'transparent',
    },
    Modal: {
      contentBg: isDark ? '#111827' : '#FFFFFF',
      headerBg: isDark ? '#111827' : '#FFFFFF',
      footerBg: isDark ? '#111827' : '#FFFFFF',
    },
    Drawer: {
      colorBgElevated: isDark ? '#111827' : '#FFFFFF',
    },
    Dropdown: {
      colorBgElevated: isDark ? '#111827' : '#FFFFFF',
      colorText: isDark ? '#E2E8F0' : '#0F172A',
    },
    Button: {
      controlHeight: 44,
      controlHeightLG: 44,
      controlHeightSM: 44,
      borderRadius: 8,
    },
    Input: {
      controlHeight: 40,
      controlHeightLG: 44,
      borderRadius: 8,
    },
    InputNumber: {
      controlHeight: 40,
      controlHeightLG: 44,
      borderRadius: 8,
    },
    DatePicker: {
      controlHeight: 40,
      controlHeightLG: 44,
      borderRadius: 8,
    },
    Select: {
      controlHeight: 40,
      controlHeightLG: 44,
      borderRadius: 8,
      optionSelectedBg: withAlpha(palette.primary, isDark ? 0.24 : 0.12),
      optionActiveBg: withAlpha(palette.primary, isDark ? 0.14 : 0.08),
    },
    Table: {
      rowHoverBg: withAlpha(palette.primary, isDark ? 0.18 : 0.04),
      borderColor: isDark ? withAlpha(palette.primary, 0.2) : '#E2E8F0',
      headerBg: withAlpha(palette.primary, isDark ? 0.2 : 0.08),
      headerColor: isDark ? '#E5E7EB' : '#0F172A',
      headerSplitColor: isDark ? withAlpha(palette.primary, 0.3) : withAlpha(palette.primary, 0.2),
    },
    Card: {
      headerBg: withAlpha(palette.primary, isDark ? 0.18 : 0.07),
      headerFontSize: 15,
      headerFontSizeSM: 14,
      colorBorderSecondary: isDark ? withAlpha(palette.primary, 0.24) : withAlpha(palette.primary, 0.16),
      extraColor: isDark ? '#E5E7EB' : '#0F172A',
    },
  };

  const overrideComponents = overrides?.components ?? {};
  const mergedComponents: NonNullable<ThemeConfig['components']> = {
    ...baseComponents,
    ...overrideComponents,
  };

  Object.keys(baseComponents).forEach((key) => {
    const componentKey = key as keyof NonNullable<ThemeConfig['components']>;
    mergedComponents[componentKey] = {
      ...(baseComponents[componentKey] ?? {}),
      ...(overrideComponents[componentKey] ?? {}),
    };
  });

  return {
    ...overrides,
    algorithm,
    token: tokens,
    components: mergedComponents,
  };
};

export const buildRootCssVariables = (
  scheme: ThemeSchemeKey,
  mode: ThemeMode,
  runtimeTheme: ThemeConfig
): Record<string, string> => {
  const palette = getSchemePalette(scheme);
  const primary = String(runtimeTheme.token?.colorPrimary ?? palette.primary);
  const secondary = String(runtimeTheme.token?.colorInfo ?? palette.secondary);
  const warm = palette.warm;
  const isDark = mode === 'dark';
  const gradient = palette.gradient ?? `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`;

  return {
    '--app-accent': primary,
    '--app-accent-2': secondary,
    '--app-accent-soft-1': withAlpha(primary, isDark ? 0.24 : 0.12),
    '--app-accent-soft-2': withAlpha(primary, isDark ? 0.18 : 0.08),
    '--app-accent-soft-3': withAlpha(primary, isDark ? 0.12 : 0.05),
    '--app-warm-soft-1': withAlpha(warm, isDark ? 0.22 : 0.14),
    '--app-warm-soft-2': withAlpha(warm, isDark ? 0.12 : 0.08),
    '--app-danger-soft-bg': isDark ? 'rgba(127, 29, 29, 0.4)' : '#FEF2F2',
    '--app-danger-soft-border': isDark ? 'rgba(248, 113, 113, 0.4)' : '#FECACA',
    '--app-danger-soft-text': isDark ? '#FCA5A5' : '#B91C1C',
    '--app-warning-soft-bg': isDark ? 'rgba(146, 64, 14, 0.4)' : '#FFFBEB',
    '--app-warning-soft-border': isDark ? 'rgba(251, 191, 36, 0.4)' : '#FDE68A',
    '--app-warning-soft-text': isDark ? '#FCD34D' : '#B45309',
    '--app-surface-opaque': isDark ? '#111827' : '#FFFFFF',
    '--app-table-header-bg': withAlpha(primary, isDark ? 0.2 : 0.08),
    '--app-card-header-bg': withAlpha(primary, isDark ? 0.18 : 0.07),
    '--app-border-soft': isDark ? 'rgba(148, 163, 184, 0.22)' : '#E2E8F0',
    '--app-shadow-soft': isDark ? '0 6px 20px rgba(2, 6, 23, 0.45)' : '0 6px 20px rgba(15, 23, 42, 0.08)',
    '--app-gradient': gradient,
  };
};